import React, { useRef, useState } from "react";
import { UploadCloud, Loader2, Camera, FolderUp } from "lucide-react";
import { compressImage } from "../lib/imageCompress";

export default function UploadZone({ onFiles, disabled, multiple = true, accept = "image/*,application/pdf" }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const processed = await Promise.all(files.map((f) => compressImage(f)));
      await onFiles(processed);
    } finally {
      setUploading(false);
      // Reset both inputs to allow selecting the same file again
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const stop = (e) => { e.stopPropagation(); };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      className={`upload-zone ${dragging ? "dragging" : ""}`}
      data-testid="upload-zone"
      style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}
    >
      {/* Hidden inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="upload-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="upload-camera-input"
      />

      {uploading ? (
        <>
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
          <div className="text-sm font-medium">Caricamento…</div>
        </>
      ) : (
        <>
          <UploadCloud className="w-8 h-8" style={{ color: "var(--text-muted)" }} />
          <div className="text-sm font-medium hidden sm:block">Trascina i file qui</div>
          <div className="flex gap-2 flex-wrap justify-center mt-1">
            <button
              type="button"
              onClick={(e) => { stop(e); cameraInputRef.current?.click(); }}
              className="btn-primary text-xs"
              data-testid="upload-camera-btn"
              title="Scatta una foto direttamente con la fotocamera"
            >
              <Camera className="w-3.5 h-3.5" /> Scatta foto
            </button>
            <button
              type="button"
              onClick={(e) => { stop(e); fileInputRef.current?.click(); }}
              className="btn-secondary text-xs"
              data-testid="upload-pick-btn"
            >
              <FolderUp className="w-3.5 h-3.5" /> Da galleria / file
            </button>
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
            Immagini (JPG, PNG, GIF, WebP) e PDF · max 25MB
          </div>
        </>
      )}
    </div>
  );
}
